import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useMyTenantsStore } from '@/stores/myTenants'
import { usePortalTenantStore } from '@/stores/portalTenant'
import { useSiteConfigStore } from '@/stores/siteConfig'
import { reloadForStaleChunk } from '@/utils/staleChunkRecovery'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/PublicLayout.vue'),
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/pages/public/Home.vue'),
        meta: { titleKey: 'meta.home' },
      },
      {
        path: 'pricing',
        name: 'pricing',
        component: () => import('@/pages/public/Pricing.vue'),
        meta: { titleKey: 'meta.pricing' },
      },
      {
        path: 'login',
        name: 'login',
        component: () => import('@/pages/public/Login.vue'),
        meta: { titleKey: 'meta.login' },
      },
      {
        path: 'register',
        name: 'register',
        component: () => import('@/pages/public/Register.vue'),
        meta: { titleKey: 'meta.register' },
      },
      {
        path: 'forgot',
        name: 'forgot',
        component: () => import('@/pages/public/ForgotPassword.vue'),
        meta: { titleKey: 'meta.login' },
      },
      {
        path: 'reset-password',
        name: 'reset',
        component: () => import('@/pages/public/ResetPassword.vue'),
        meta: { titleKey: 'meta.login' },
      },
      {
        path: 'verify-email',
        name: 'verify',
        component: () => import('@/pages/public/VerifyEmail.vue'),
        meta: { titleKey: 'meta.register' },
      },
      {
        path: 'confirm-email',
        name: 'confirm-email',
        component: () => import('@/pages/public/ConfirmEmail.vue'),
        meta: { titleKey: 'meta.confirmEmail' },
      },
      {
        path: 'terms',
        name: 'terms',
        component: () => import('@/pages/public/Terms.vue'),
        meta: { titleKey: 'meta.terms' },
      },
      {
        path: 'acceptable-use',
        name: 'acceptable-use',
        component: () => import('@/pages/public/AcceptableUse.vue'),
        meta: { titleKey: 'meta.acceptableUse' },
      },
    ],
  },
  {
    // Mandatory company-profile step. It sits outside the portal shell on purpose: the
    // account is signed in but has not been admitted to the portal yet.
    path: '/complete-profile',
    name: 'complete-profile',
    component: () => import('@/pages/public/CompleteProfile.vue'),
    meta: { requiresAuth: true, titleKey: 'meta.completeProfile' },
  },
  {
    path: '/app',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: { name: 'overview' } },
      {
        path: 'overview',
        name: 'overview',
        component: () => import('@/pages/app/Overview.vue'),
        meta: { titleKey: 'meta.app', breadcrumb: 'nav.overview' },
      },
      {
        path: 'security',
        name: 'security',
        component: () => import('@/pages/app/Security.vue'),
        meta: { titleKey: 'meta.app', breadcrumb: 'nav.security' },
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/pages/app/Profile.vue'),
        meta: { titleKey: 'meta.app', breadcrumb: 'nav.profile' },
      },
      {
        path: 'preferences',
        name: 'preferences',
        component: () => import('@/pages/app/Preferences.vue'),
        meta: { titleKey: 'meta.app', breadcrumb: 'nav.preferences' },
      },
      {
        path: 'notifications',
        name: 'notifications',
        component: () => import('@/pages/app/Notifications.vue'),
        meta: { titleKey: 'meta.app', breadcrumb: 'nav.notifications' },
      },
      {
        path: 'support',
        name: 'support',
        component: () => import('@/pages/app/Support.vue'),
        meta: { titleKey: 'meta.app', breadcrumb: 'nav.support' },
      },
      {
        path: 'support/new',
        name: 'support-new',
        component: () => import('@/pages/app/SupportNew.vue'),
        meta: { titleKey: 'meta.app', breadcrumb: 'nav.support' },
      },
      {
        path: 'support/:id(\\d+)',
        name: 'support-detail',
        component: () => import('@/pages/app/SupportDetail.vue'),
        meta: { titleKey: 'meta.app', breadcrumb: 'nav.support' },
      },
      {
        path: 'onboarding',
        name: 'onboarding',
        component: () => import('@/pages/app/Onboarding.vue'),
        meta: { titleKey: 'meta.app', breadcrumb: 'nav.onboarding' },
      },
      {
        path: 'tenants',
        name: 'tenants',
        component: () => import('@/pages/app/tenants/TenantsList.vue'),
        meta: { titleKey: 'meta.app', breadcrumb: 'nav.tenants' },
      },
      {
        path: 'tenants/new',
        name: 'tenant-new',
        component: () => import('@/pages/app/tenants/CreateTenant.vue'),
        meta: { titleKey: 'meta.app', breadcrumb: 'app.tenants.create.title' },
      },
      {
        path: 'tenants/:id/suspended',
        name: 'tenant-suspended',
        component: () => import('@/pages/app/tenants/TenantSuspended.vue'),
        meta: { titleKey: 'meta.app', breadcrumb: 'app.tenants.status.suspended' },
      },
      {
        path: 'tenants/:id',
        component: () => import('@/pages/app/tenants/TenantDetailLayout.vue'),
        meta: { titleKey: 'meta.app', breadcrumb: 'nav.tenants' },
        children: [
          {
            path: '',
            name: 'tenant-overview',
            component: () => import('@/pages/app/tenants/TenantDashboard.vue'),
            meta: { breadcrumb: 'app.tenant.tabs.overview' },
          },
          {
            path: 'subscription',
            name: 'tenant-subscription',
            component: () => import('@/pages/app/tenants/TenantSubscription.vue'),
            meta: { tenantAdmin: true, breadcrumb: 'app.tenant.tabs.subscription' },
          },
          {
            path: 'invoices',
            name: 'tenant-invoices',
            component: () => import('@/pages/app/tenants/TenantInvoices.vue'),
            meta: { tenantAdmin: true, breadcrumb: 'app.tenant.tabs.invoices' },
          },
          {
            path: 'addons',
            name: 'tenant-addons',
            component: () => import('@/pages/app/tenants/TenantAddons.vue'),
            meta: { tenantAdmin: true, breadcrumb: 'app.tenant.tabs.addons' },
          },
          {
            path: 'domains',
            name: 'tenant-domains',
            component: () => import('@/pages/app/tenants/TenantDomains.vue'),
            meta: { tenantAdmin: true, breadcrumb: 'app.tenant.tabs.domains' },
          },
          {
            path: 'usage',
            name: 'tenant-usage',
            component: () => import('@/pages/app/tenants/TenantUsage.vue'),
            meta: { breadcrumb: 'app.tenant.tabs.usage' },
          },
          {
            path: 'team',
            name: 'tenant-team',
            component: () => import('@/pages/app/Team.vue'),
            meta: { breadcrumb: 'app.tenant.tabs.team' },
          },
          {
            path: 'roles',
            name: 'tenant-roles',
            component: () => import('@/pages/app/Roles.vue'),
            meta: { tenantAdmin: true, breadcrumb: 'app.tenant.tabs.roles' },
          },
          {
            path: 'api-keys',
            name: 'tenant-api-keys',
            component: () => import('@/pages/app/ApiKeys.vue'),
            meta: { tenantAdmin: true, breadcrumb: 'app.tenant.tabs.apiKeys' },
          },
          {
            path: 'audit',
            name: 'tenant-audit',
            component: () => import('@/pages/app/Audit.vue'),
            meta: { tenantAdmin: true, breadcrumb: 'app.tenant.tabs.audit' },
          },
          {
            path: 'settings',
            name: 'tenant-settings',
            component: () => import('@/pages/app/tenants/TenantOrgSettings.vue'),
            meta: { tenantAdmin: true, breadcrumb: 'app.tenant.tabs.settings' },
          },
        ],
      },
      {
        path: 'team',
        redirect: () => {
          const tid = useAuthStore().tenantId
          return tid ? { path: `/app/tenants/${tid}/team` } : { name: 'tenants' }
        },
      },
      {
        path: 'roles',
        redirect: () => {
          const tid = useAuthStore().tenantId
          return tid ? { path: `/app/tenants/${tid}/roles` } : { name: 'tenants' }
        },
      },
      {
        path: 'settings',
        redirect: () => {
          const tid = useAuthStore().tenantId
          return tid ? { path: `/app/tenants/${tid}/settings` } : { name: 'tenants' }
        },
      },
      {
        path: 'billing',
        redirect: () => {
          const tid = useAuthStore().tenantId
          return tid ? { path: `/app/tenants/${tid}/subscription` } : { name: 'tenants' }
        },
      },
      {
        path: 'api-keys',
        redirect: () => {
          const tid = useAuthStore().tenantId
          return tid ? { path: `/app/tenants/${tid}/api-keys` } : { name: 'tenants' }
        },
      },
      {
        path: 'audit',
        redirect: () => {
          const tid = useAuthStore().tenantId
          return tid ? { path: `/app/tenants/${tid}/audit` } : { name: 'tenants' }
        },
      },
    ],
  },
  {
    path: '/forbidden',
    name: 'forbidden',
    component: () => import('@/pages/ErrorForbidden.vue'),
    meta: { titleKey: 'meta.forbidden' },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/pages/ErrorNotFound.vue'),
    meta: { titleKey: 'meta.notFound' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
})

router.onError((error) => {
  reloadForStaleChunk(error, 'router')
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()
  const myTenants = useMyTenantsStore()
  const siteConfig = useSiteConfigStore()
  const portalTenant = usePortalTenantStore()

  // On custom domains, auto-land on the resolved tenant page.
  // The Worker already injected window.__PORTAL_TENANT__ so no async call needed.
  if (
    portalTenant.isCustomDomain &&
    (to.path === '/' || to.path === '/app' || to.path === '/app/')
  ) {
    return { path: `/app/tenants/${portalTenant.tenantId}`, replace: true }
  }

  if (!siteConfig.loaded) {
    await siteConfig.load()
  }
  if (!auth.bootstrapped) {
    await auth.bootstrap()
  }
  if (to.matched.some((r) => r.meta.requiresAuth) && !auth.isAuthenticated) {
    const query: Record<string, string> = { redirect: to.fullPath }
    if (portalTenant.isCustomDomain && portalTenant.tenantId) {
      query.tid = String(portalTenant.tenantId)
    }
    return { name: 'login', query }
  }
  if (to.matched.some((r) => r.meta.requiresAuth) && auth.isAuthenticated) {
    // The company profile is mandatory: hold the account here until it is stored. The
    // attempted destination is kept so the user resumes where they meant to go.
    if (!auth.profileComplete) {
      if (to.name === 'complete-profile') return true
      return { name: 'complete-profile', query: { redirect: to.fullPath } }
    }
    if (to.name === 'complete-profile') {
      return { name: 'overview' }
    }
    if (!myTenants.loaded) {
      await myTenants.fetchMyTenants()
    }
    // Workspace setup is optional, so a signed-in account owning nothing is free to
    // browse the portal; it is only sent back to onboarding while a workspace is
    // genuinely blocked on missing ERP admin credentials.
    const blockedOnErpCredentials = myTenants.tenants.some(
      (t) => t.is_owner && t.erp_provisioning_state === 'needs_credentials',
    )
    if (blockedOnErpCredentials && to.name !== 'onboarding' && to.path.startsWith('/app')) {
      return { name: 'onboarding', query: { redirect: to.fullPath } }
    }
  }
  const idRaw = to.params.id
  if (typeof idRaw === 'string' && /^\d+$/.test(idRaw) && to.path.startsWith('/app/tenants/')) {
    const tid = Number(idRaw)
    const row = myTenants.memberRow(tid)
    if (!row) {
      return { name: 'forbidden' }
    }
    if (row.status === 'suspended') {
      const allowedWhenSuspended = ['tenant-suspended', 'tenant-invoices']
      if (!allowedWhenSuspended.includes(to.name as string)) {
        return { name: 'tenant-suspended', params: { id: idRaw } }
      }
    }
    if (to.matched.some((record) => record.meta.tenantAdmin) && !row.is_admin) {
      return { name: 'forbidden' }
    }
  }
  return true
})

export default router
